import { useShowNotification } from '@inflearn/ds-react';
import type { SelectedCategory } from '@inflearn/modals';
import {
  MyCategoryModal,
  MyCategorySelectView,
  MyCategorySkeletonView,
  QUERY_STRING_KEYS,
  QUERY_STRING_VALUES,
  UnauthorizedModal,
  useIsFirstTo,
  useMyCategoryCache,
} from '@inflearn/modals';
import { useDisclosure } from '@mantine/hooks';
import { isEqual } from 'lodash-es';
import querystring from 'query-string';
import { useEffect } from 'react';

import type { MyCategoryResponse } from '../../@types/uccSchema';
import {
  API_ENDPOINT,
  ResponseStatus,
  useV2CourseCategoryQuery,
  useV2MyCategoryQuery,
  useV2MyCategoryUpdateMutation,
  useV2SessionQuery,
} from '../../@types/uccSchema';
import { eventNames } from '../../constants/gtmEventNames';
import { EVENT_ID } from '../../constants/MODAL_EVENT_ID';
import { SERVICE_URLS } from '../../constants/urls';
import { useForAntManModalDisclosure } from '../../hooks/useForAntManModalDisclosure';
import { useTrackEvent } from '../../utils/useTrackEvent';
import { ApiError } from '@inflearn/datasource-rest';

export default function MyCategoryModalWrapper() {
  const { data: session } = useV2SessionQuery({
    queryKey: [API_ENDPOINT.V2_SESSION],
    url: API_ENDPOINT.V2_SESSION,
  });

  if (!session?.userId) {
    return null;
  }

  return <_MyCategoryModal />;
}

function _MyCategoryModal() {
  const { trackEvent } = useTrackEvent();
  const [isMyCategoryModalOpened, myCategoryModalHandlers] = useForAntManModalDisclosure({
    eventId: EVENT_ID.MY_CATEGORY_MODAL,
  });

  const [isUnauthorizedModalOpened, unauthorizedModalHandlers] = useDisclosure(false);

  const isFirstRegister = useIsFirstTo({
    enabled: isMyCategoryModalOpened,
    valueKey: QUERY_STRING_VALUES.IS_FIRSTREGISTER,
    onSuccessPipe: (searchParams) => {
      searchParams.delete(QUERY_STRING_KEYS.TO);

      return searchParams;
    },
  });
  const isCourseListSpot = useIsFirstTo({
    enabled: isMyCategoryModalOpened,
    valueKey: QUERY_STRING_VALUES.COURSE_LIST,
    onSuccessPipe: (searchParams) => {
      searchParams.delete(QUERY_STRING_KEYS.TO);

      return searchParams;
    },
  });
  const isAccountSpot = useIsFirstTo({
    enabled: isMyCategoryModalOpened,
    valueKey: QUERY_STRING_VALUES.ACCOUNT,
    onSuccessPipe: (searchParams) => {
      searchParams.delete(QUERY_STRING_KEYS.TO);

      return searchParams;
    },
  });
  const myCategorySpot = isCourseListSpot ? 'course_list' : isAccountSpot ? 'account' : null;

  const { showNotification } = useShowNotification();
  const { data: session } = useV2SessionQuery({
    queryKey: [API_ENDPOINT.V2_SESSION],
    url: API_ENDPOINT.V2_SESSION,
  });
  const { data: myCategories } = useV2MyCategoryQuery({
    queryKey: [API_ENDPOINT.V2_MY_CATEGORY],
    url: API_ENDPOINT.V2_MY_CATEGORY,
    options: {
      enabled: !!session?.userId,
    },
  });
  const { data: courseCategories } = useV2CourseCategoryQuery({
    queryKey: [API_ENDPOINT.V2_COURSE_CATEGORY],
    url: API_ENDPOINT.V2_COURSE_CATEGORY,
    options: {
      staleTime: Infinity,
      enabled: !!session?.userId,
    },
  });
  const { setCache } = useMyCategoryCache(session?.userId);

  const { mutate, isLoading: isLoadingForUpdateMyCategory } = useV2MyCategoryUpdateMutation({
    url: API_ENDPOINT.V2_MY_CATEGORY,
  });

  useEffect(() => {
    if (!(isMyCategoryModalOpened && myCategories)) {
      return;
    }

    const _myCategory = myCategories.map((category) =>
      isChildCategory(category)
        ? `${category.parent.title} > ${category.title}`
        : `${category.title} > 전체`
    );

    void trackEvent(eventNames.VIEW_MYCATEGORY, {
      is_firstregister: isFirstRegister,
      mycategory: _myCategory,
      mycategory_spot: myCategorySpot,
    });
  }, [isMyCategoryModalOpened, myCategories]);

  const handleModalClose = () => {
    myCategoryModalHandlers.close();
    setCache();
  };

  const handleSubmit = (categories: SelectedCategory[]) => {
    const prevCategoryIds = myCategories?.map((category) => category.id);
    const categoryIds = categories.map((category) => category.id);

    if (isEqual(prevCategoryIds, categories)) {
      handleModalClose();

      return;
    }
    mutate(
      {
        categoryIds,
      },
      {
        onSuccess: () => {
          handleModalClose();

          const _categories = categories.map((category) => {
            if (category.type === 'PARENT') {
              return `${category.title} > 전체`;
            }

            const parentCategoryTitle =
              courseCategories?.find((c) => c.children.find((cc) => cc.id === category.id))
                ?.title || '';

            return `${parentCategoryTitle} > ${category.title}`;
          });

          void trackEvent(eventNames.CLICK_SAVE_MYCATEGORY, {
            is_firstregister: isFirstRegister,
            mycategory: _categories,
            mycategory_spot: myCategorySpot,
          });

          if (isFirstRegister && window.location.pathname === SERVICE_URLS.COURSES) {
            window.location.href = querystring.stringifyUrl({
              url: SERVICE_URLS.COURSES_MY_CATEGORY,
              query: { charge: 'free', order: 'seq' },
            });

            return;
          }
          window.location.reload();
        },
        onError: (error) => {
          if (!(error instanceof ApiError)) {
            showNotification({
              type: 'error',
              title: '처리 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
            });

            return;
          }

          if (error.statusCode === ResponseStatus.UNAUTHORIZED) {
            myCategoryModalHandlers.close();
            unauthorizedModalHandlers.open();

            return;
          }

          showNotification({
            type: 'error',
            title: '처리 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
          });
        },
      }
    );
  };

  const handleUnauthorizedModalClose = () => {
    const referUrl = window.location.href;

    window.location.href = querystring.stringifyUrl({
      url: SERVICE_URLS.USER_SIGNIN,
      query: { referUrl: encodeURIComponent(referUrl) },
    });
  };

  const hasMyCategory = !!myCategories && myCategories?.length !== 0;

  return (
    <>
      <MyCategoryModal
        lang="ko"
        isOpened={isMyCategoryModalOpened}
        closeOnClickOutside={hasMyCategory}
        onClose={myCategoryModalHandlers.close}
        hideLaterButton={isFirstRegister}>
        {!myCategories || !courseCategories ? (
          <MyCategorySkeletonView />
        ) : (
          <MyCategorySelectView
            courseCategories={courseCategories}
            myCategories={myCategories}
            onSave={handleSubmit}
            isLoading={isLoadingForUpdateMyCategory}
          />
        )}
      </MyCategoryModal>
      <UnauthorizedModal
        lang="ko"
        isOpened={isUnauthorizedModalOpened}
        handleClose={handleUnauthorizedModalClose}
      />
    </>
  );
}

const isChildCategory = (category: MyCategoryResponse): category is Required<MyCategoryResponse> =>
  category.type === 'CHILD';
